import {
	Alert,
	AlertDescription,
	Button,
	Card,
	CardContent,
	CardHeader,
	Input,
	Skeleton,
	Switch,
	TooltipProvider,
} from "@ancatag/at-design";
import { AlertCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useLocation } from "wouter";
import {
	ConfigCard,
	type ConfigCardActions,
	type ConfigCardData,
} from "../components/configs/ConfigCard";
import { type ConnectionStatus } from "../components/configs/ConnectionStatusBadge";
import {
	deleteRouteConfig,
	listMyRouteConfigs,
	type RouteConfig,
	testRouteConfigConnection,
} from "../services/route-configs";

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// MODEL CONFIG CARD
// ═══════════════════════════════════════════════════════════════════════════

function RouteConfigCard({
	config,
	onTest,
	onDelete,
	onEdit,
	onRag,
}: {
	config: RouteConfig;
	onTest: (id: string) => void;
	onDelete: (id: string) => void;
	onEdit: (id: string) => void;
	onRag: (id: string) => void;
}) {
	// Get primary model config for status and details
	const primaryModelConfig = config.models?.find(
		(m) => m.isPrimary,
	)?.modelConfig;
	const connectionStatus = (primaryModelConfig?.connectionStatus ||
		"unknown") as ConnectionStatus;

	// Map RouteConfig to ConfigCardData
	const cardData: ConfigCardData = {
		id: config.id,
		name: config.name,
		description: config.description,
		generationType: config.generationType,
		isActive: config.isActive,
		connectionStatus,
		connectionError: primaryModelConfig?.connectionError || null,
		providerType: primaryModelConfig?.providerType,
		modelIdentifier: primaryModelConfig?.modelIdentifier,
		modelDisplayName: primaryModelConfig?.modelDisplayName,
		isHosted: primaryModelConfig?.isHosted,
		isNsfw: config.isNsfw,
		nsfwLevel: config.nsfwLevel,
		requiresAgeVerification: config.requiresAgeVerification,
		modelCount: config.models?.length,
		hotCacheEnabled: config.hotCacheEnabled,
		semanticCacheEnabled: config.semanticCacheEnabled,
		ragEnabled: config.ragEnabled,
	};

	const cardActions: ConfigCardActions = {
		onEdit: () => onEdit(config.id),
		onTest: async () => onTest(config.id),
		onDelete: async () => onDelete(config.id),
		onRag: config.ragEnabled ? () => onRag(config.id) : undefined,
	};

	return <ConfigCard data={cardData} actions={cardActions} type="route" />;
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function Models() {
	const [, navigate] = useLocation();
	const [configs, setConfigs] = useState<RouteConfig[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [search, setSearch] = useState("");
	const [showNsfw, setShowNsfw] = useState(true); // Show NSFW by default

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);
			const configsRes = await listMyRouteConfigs({ limit: 50 });
			setConfigs(configsRes.data);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to load data");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const handleEdit = (configId: string) => {
		navigate(`/route-configs/${configId}/edit`);
	};

	const handleRag = (configId: string) => {
		navigate(`/route-configs/${configId}/rag`);
	};

	const handleTest = async (configId: string) => {
		try {
			const result = await testRouteConfigConnection(configId);
			if (result.success) {
				toast.success(`Ping successful! (${result.responseTime}ms)`);
			} else {
				toast.error(`Ping failed: ${result.error || "Unknown error"}`);
			}
			fetchData(); // Refresh to show updated status
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Ping failed");
		}
	};

	const handleDelete = async (configId: string) => {
		if (
			!confirm(`Delete this route configuration? This action cannot be undone.`)
		)
			return;
		try {
			await deleteRouteConfig(configId);
			toast.success("Route configuration deleted");
			fetchData();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Delete failed");
		}
	};

	const filteredConfigs = (configs || []).filter((c) => {
		const primaryModelConfig = c.models?.find((m) => m.isPrimary)?.modelConfig;
		const matchesSearch =
			c.name.toLowerCase().includes(search.toLowerCase()) ||
			primaryModelConfig?.providerType
				.toLowerCase()
				.includes(search.toLowerCase()) ||
			primaryModelConfig?.modelIdentifier
				.toLowerCase()
				.includes(search.toLowerCase());
		const matchesNsfwFilter = showNsfw || !c.isNsfw;
		return matchesSearch && matchesNsfwFilter;
	});

	return (
		<TooltipProvider>
			<div className="container mx-auto max-w-7xl py-8 px-4 sm:px-6 lg:px-8">
				<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
					<div>
						<h1 className="text-2xl md:text-3xl font-bold text-foreground">
							Route Configurations
						</h1>
						<p className="text-muted-foreground mt-2 text-sm md:text-base">
							Manage your route configurations. Route configs define how
							requests are routed to multiple model connections with failover,
							load balancing, and caching strategies.
						</p>
					</div>
					<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
						<Button
							variant="outline"
							onClick={() => navigate("/playground")}
							className="w-full sm:w-auto"
						>
							Go to Playground
						</Button>
						<Button
							onClick={() => navigate("/route-configs/new")}
							className="w-full sm:w-auto"
						>
							+ New Route Configuration
						</Button>
					</div>
				</div>

				{error && (
					<Alert variant="destructive" className="mb-6">
						<AlertCircle className="h-4 w-4" />
						<AlertDescription>{error}</AlertDescription>
					</Alert>
				)}

				{/* Search and Filters */}
				<div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
					<Input
						placeholder="Search configs..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="max-w-sm"
					/>
					<div className="flex items-center gap-2">
						<Switch
							id="show-nsfw"
							checked={showNsfw}
							onCheckedChange={setShowNsfw}
						/>
						<label
							htmlFor="show-nsfw"
							className="text-sm text-muted-foreground cursor-pointer"
						>
							Show NSFW models
						</label>
					</div>
				</div>

				{/* Configs Grid */}
				{loading ? (
					<div className="grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
						{[...Array(6)].map((_, i) => (
							<Card key={i}>
								<CardHeader className="pb-3">
									<Skeleton className="h-5 w-32 mb-2" />
									<Skeleton className="h-4 w-full" />
								</CardHeader>
								<CardContent>
									<Skeleton className="h-20 w-full" />
								</CardContent>
							</Card>
						))}
					</div>
				) : filteredConfigs.length === 0 ? (
					<div className="p-8 border-2 border-dashed border-border rounded-lg text-center space-y-3">
						<p className="text-muted-foreground">
							{search
								? "No route configurations match your search."
								: "No route configurations yet. Create your first one with the wizard."}
						</p>
						<div className="flex justify-center gap-2">
							<Button onClick={() => navigate("/route-configs/new")}>
								Open Wizard
							</Button>
							<Button variant="outline" onClick={() => navigate("/playground")}>
								Back to Playground
							</Button>
						</div>
					</div>
				) : (
					<div className="grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
						{filteredConfigs.map((config) => (
							<RouteConfigCard
								key={config.id}
								config={config}
								onTest={handleTest}
								onDelete={handleDelete}
								onEdit={handleEdit}
								onRag={handleRag}
							/>
						))}
					</div>
				)}
			</div>
		</TooltipProvider>
	);
}
